import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MarketingNav from "@/components/site/MarketingNav";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { getRecaptchaToken, isRecaptchaConfigured } from "@/lib/recaptcha";
import PasswordStrength from "@/components/site/PasswordStrength";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [refCode, setRefCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("ref");
    if (fromUrl) {
      const clean = fromUrl.toLowerCase();
      localStorage.setItem("draftwave.ref_code", clean);
      setRefCode(clean);
      return;
    }
    const stored = localStorage.getItem("draftwave.ref_code");
    if (stored) setRefCode(stored);
  }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let user;
      if (mode === "login") {
        user = await login(email, password);
      } else {
        let recaptcha_token = null;
        try {
          recaptcha_token = await getRecaptchaToken("signup");
        } catch (captchaErr) {
          throw new Error("Human verification failed to load — please refresh and try again.");
        }
        user = await register({ email, password, name, referral_code: refCode || undefined, recaptcha_token });
        localStorage.removeItem("draftwave.ref_code");
      }
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      const pendingPlan = localStorage.getItem("draftwave.pending_plan");
      if (pendingPlan) {
        localStorage.removeItem("draftwave.pending_plan");
        navigate(`/billing?plan=${pendingPlan}`);
      } else if (user?.is_admin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      const msg = formatError(e?.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      <MarketingNav />
      <main className="flex-1 grid grid-cols-1 md:grid-cols-2 border-b border-border">
        <div className="p-8 md:p-16 flex flex-col justify-center border-r border-border">
          <div className="overline text-muted-foreground">{mode === "login" ? "Sign in" : "Create account"}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-none tracking-tight mt-4">
            {mode === "login" ? (
              <>
                Welcome
                <br />
                back.
              </>
            ) : (
              <>
                Start
                <br />
                writing.
              </>
            )}
          </h1>
          <p className="mt-6 max-w-md text-muted-foreground">
            {mode === "login"
              ? "Sign in with your Draftwave credentials."
              : "Get the studio, the audience, and the recurring revenue engine. Cancel any time."}
          </p>

          <form onSubmit={submit} className="mt-10 max-w-md space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="overline text-muted-foreground">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="rounded-none mt-2 h-11 font-mono text-sm"
                  data-testid="input-name"
                />
              </div>
            )}
            <div>
              <label className="overline text-muted-foreground">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="rounded-none mt-2 h-11 font-mono text-sm"
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground">Password</label>
              <div className="relative mt-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={mode === "register" ? 8 : undefined}
                  placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                  className="rounded-none h-11 font-mono text-sm pr-11"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === "register" && <PasswordStrength password={password} />}
              {mode === "login" && (
                <div className="mt-2 text-right">
                  <Link
                    to="/forgot"
                    className="text-xs text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>
            {mode === "register" && (
              <div>
                <label className="overline text-muted-foreground">Referral code (optional)</label>
                <Input
                  value={refCode}
                  onChange={(e) => setRefCode(e.target.value)}
                  placeholder="From a friend"
                  className="rounded-none mt-2 h-11 font-mono text-sm"
                  data-testid="input-ref"
                />
              </div>
            )}
            {error && (
              <div className="text-sm text-destructive font-mono" data-testid="auth-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="rounded-none h-12 px-8 font-mono uppercase tracking-widest text-xs w-full md:w-auto"
              data-testid="auth-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError(null);
              }}
              className="text-primary underline hover-invert"
              data-testid="toggle-mode-btn"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
          <p className="mt-6 text-xs text-muted-foreground max-w-md">
            By continuing you agree to our{" "}
            <Link to="/legal/terms" className="underline">Terms</Link> and{" "}
            <Link to="/legal/privacy" className="underline">Privacy</Link> policy.
          </p>
          {mode === "register" && isRecaptchaConfigured() && (
            <p className="mt-3 text-[10px] text-muted-foreground/70 max-w-md font-mono uppercase tracking-widest" data-testid="recaptcha-notice">
              Protected by reCAPTCHA · Google{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a>{" "}
              &{" "}
              <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms</a>
            </p>
          )}
        </div>
        <div className="relative overflow-hidden hidden md:block bg-foreground">
          <img
            src="https://images.unsplash.com/photo-1632187989763-c9c620420b4d?w=1200&auto=format&fit=crop&q=80"
            alt="Editorial studio"
            className="absolute inset-0 w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent" />
          <div className="absolute bottom-10 left-10 right-10">
            <div className="overline text-background/70">On the wall</div>
            <div className="font-serif-display text-3xl md:text-5xl text-background leading-tight mt-4">
              "Write like you're building a canon, not a content mill."
            </div>
            <div className="mt-6 overline text-background/60">— Draftwave editorial standard</div>
          </div>
        </div>
      </main>
    </div>
  );
}
